import { useCallback, useRef } from 'react';

/**
 * Clave nueva para un intento lógico. `crypto.randomUUID` solo existe en
 * contextos seguros (https o localhost): fuera de ellos se envía sin clave, que
 * es el comportamiento de siempre del alta (el backend no la exige).
 * @returns {string|null}
 */
function newIdempotencyKey() {
  const cryptoImpl = typeof window !== 'undefined' ? window.crypto : undefined;
  return typeof cryptoImpl?.randomUUID === 'function' ? cryptoImpl.randomUUID() : null;
}

/**
 * Una clave de idempotencia por "huella" del payload.
 *
 * `keyFor(huella)` devuelve la misma clave mientras la huella no cambie: así un
 * reintento tras una caída de red o un timeout (cuando no se sabe si el backend
 * creó la incidencia) viaja con la misma clave y el backend responde el
 * original en vez de duplicar. Si la huella cambia es otro intento lógico y se
 * genera otra clave: reusarla con otro payload daría 422 en el backend.
 * `reset()` la descarta: tras un éxito, o cuando el backend ya no la acepta
 * (422 INCIDENT_IDEMPOTENCY_KEY_REUSED). Después de otros rechazos se conserva:
 * no quedó guardada y es la única forma de que el reintento de un envío dudoso
 * no duplique.
 *
 * La clave vive en un ref: cambiarla no tiene por qué re-renderizar.
 *
 * @returns {{ keyFor: (fingerprint: string) => string|null, reset: () => void }}
 */
export function useIdempotencyKey() {
  const currentRef = useRef({ fingerprint: null, key: null });

  const keyFor = useCallback((fingerprint) => {
    const current = currentRef.current;
    if (current.key === null || current.fingerprint !== fingerprint) {
      currentRef.current = { fingerprint, key: newIdempotencyKey() };
    }
    return currentRef.current.key;
  }, []);

  const reset = useCallback(() => {
    currentRef.current = { fingerprint: null, key: null };
  }, []);

  return { keyFor, reset };
}

export default useIdempotencyKey;
